import { MouseEventHandler, useCallback, useEffect } from 'react';

import classNames from 'classnames';
import { AnimatePresence, Variant, motion } from 'framer-motion';
import vhCheck from 'vh-check';

import useDeviceType from '../../../providers/DeviceType/hooks';
import ComponentType from '../../../types/Component';

import styles from './styles.module.scss';

type ModalType = {
	visible?: boolean;
	onClose?: () => void;
	layoutId?: string;
	ownBody?: boolean;
	className?: string;
	appearance?: 'light' | 'dark';
	animationVariants?: {
		visible: Variant;
		hidden: Variant;
	};
};

const Modal: ComponentType<ModalType> = ({
	visible,
	children,
	onClose,
	layoutId,
	className,
	ownBody,
	animationVariants,
	appearance = 'dark',
}) => {
	const { isDesktop } = useDeviceType();
	const keyPressHandler = useCallback(
		(event: any) => {
			if (!visible) return;
			if (event.code === 'Escape') {
				onClose?.();
			}
		},
		[visible]
	);
	const backgroundClickHandler: MouseEventHandler<HTMLDivElement> = ({ target, currentTarget }) => {
		if (target === currentTarget) onClose?.();
	};

	useEffect(() => {
		if (visible) {
			vhCheck({
				force: true,
			});
			document.body.style.overflow = 'hidden';
			if (isDesktop) {
				document.body.classList.add('no-overflow-visible');
			}
		} else {
			setTimeout(() => {
				document.body.classList.remove('no-overflow-visible');
				document.body.style.overflow = 'auto';
			}, 300);
		}
		window?.addEventListener('keydown', keyPressHandler);
		return () => {
			window?.removeEventListener('keydown', keyPressHandler);
		};
	}, [visible, isDesktop]);

	return (
		<AnimatePresence>
			{visible && (
				<motion.div
					key={'modal-background'}
					className={classNames([
						styles.modal,
						className,
						appearance === 'light' && styles.modalLight,
					])}
					variants={{
						visible: { opacity: 1, position: 'fixed', zIndex: 99 },
						hidden: { opacity: 0, position: 'fixed', zIndex: 99 },
					}}
					initial={'hidden'}
					transition={{ duration: 0.3 }}
					exit={'hidden'}
					animate={'visible'}
					onClick={backgroundClickHandler}>
					{ownBody && <>{children}</>}
					{!ownBody && (
						<motion.div
							variants={
								animationVariants ?? {
									visible: { opacity: 1, y: 0 },
									hidden: { opacity: 0, y: '-10%' },
								}
							}
							layoutId={layoutId}
							initial={'hidden'}
							exit={'hidden'}
							animate={'visible'}
							transition={{ duration: 0.3 }}
							key={'modal-body'}>
							{children}
						</motion.div>
					)}
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default Modal;
